import { useMutation } from '@apollo/client'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Loader,
  NotificationType,
  Pagination,
} from '../../../../components'
import {
  ButtonColor,
  ButtonSize,
  ButtonStyle,
} from '../../../../components/Button'
import { Toggle } from '../../../../components/Toggle'
import { Avatar } from '../../../../elements'
import {
  GET_USER_BRANDS,
  MutationUpdateUserBrandResponse,
  UPDATE_USER_BRAND,
} from '../../../../gql'
import {
  Pagination as IPagination,
  MutationUpdateUserBrandArgs,
  User,
  UserBrand,
} from '../../../../gql/types'
import { useNotification } from '../../../../utils/hooks'
import { BrandRoutes } from '../../../BrandRoutes'
import classnames from 'classnames'

const TableHead = () => {
  return (
    <thead className='bg-white border-t border-gray-200'>
      <tr>
        <th
          scope='col'
          className='py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-600 sm:pl-6'
        >
          Brand
        </th>

        <th
          scope='col'
          className='px-3 py-3.5 text-left text-sm font-semibold text-gray-600'
        >
          Menu Visibility
        </th>

        <th
          scope='col'
          className='px-3 py-3.5 text-left text-sm font-semibold text-gray-600'
        >
          Show/Hide
        </th>
        <th
          scope='col'
          className='px-3 py-3.5 text-left text-sm font-semibold text-gray-600'
        >
          Access
        </th>
      </tr>
    </thead>
  )
}

interface IProps {
  userBrands: UserBrand[]
  pagination: IPagination
  setPage: (page: number) => void
  loading: boolean
  user: User
}

const List = ({ userBrands, pagination, setPage, loading, user }: IProps) => {
  const { addNotification } = useNotification()
  const navigate = useNavigate()
  const [updatingBrandId, setUpdatingBrandId] = useState<number | null>(null)

  const [updateUserBrand] = useMutation<
    MutationUpdateUserBrandResponse,
    MutationUpdateUserBrandArgs
  >(UPDATE_USER_BRAND, {
    refetchQueries: [
      {
        query: GET_USER_BRANDS,
        variables: {
          options: {
            filters: {
              userId: user.id,
              isMenuVisible: true,
            },
            limit: 20,
          },
        },
      },
    ],
    onCompleted: () => {
      addNotification(
        'Brand visibility updated successfully',
        NotificationType.success,
      )
      setUpdatingBrandId(null)
    },
    onError: (error) => {
      addNotification(
        error.message || 'Failed to update brand visibility',
        NotificationType.error,
      )
      setUpdatingBrandId(null)
    },
  })

  const handleToggleVisibility = async (
    userBrand: UserBrand,
    isVisible: boolean,
  ) => {
    setUpdatingBrandId(userBrand.id)

    try {
      await updateUserBrand({
        variables: {
          userBrandId: userBrand.id,
          input: { isMenuVisible: isVisible },
        },
      })
    } catch (error) {
      console.error('Error updating brand visibility:', error)
    }
  }

  const handleLoginToBrand = (brandId: number) => {
    const campaignsUrl = BrandRoutes.campaigns.replace(
      ':id',
      brandId.toString(),
    )
    navigate(campaignsUrl)
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div className='flex flex-col'>
      <table className='min-w-full divide-y divide-gray-200'>
        <TableHead />
        <tbody className='divide-y divide-gray-200 bg-white'>
          {!userBrands || userBrands.length === 0 ? (
            <tr>
              <td className='px-6 py-4 text-sm text-gray-500'>
                No brands found.
              </td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          ) : (
            userBrands.map((userBrand, index) => (
              <tr
                key={userBrand.id}
                className={classnames(
                  index % 2 === 0 ? 'bg-gray-50' : 'bg-white',
                )}
              >
                <td className='whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6'>
                  <div className='flex items-center'>
                    <div className='h-10 w-10 flex-shrink-0'>
                      <Avatar
                        type='brand'
                        src={userBrand.brand.logo.url}
                        size='sm'
                        alt={userBrand.brand.name}
                      />
                    </div>
                    <div className='ml-4'>
                      <div className='text-sm font-medium text-gray-900'>
                        {userBrand.brand.name}
                      </div>
                    </div>
                  </div>
                </td>

                <td className='whitespace-nowrap px-3 py-4 text-sm'>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      userBrand.isMenuVisible
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {userBrand.isMenuVisible ? 'Visible' : 'Hidden'}
                  </span>
                </td>

                <td className='whitespace-nowrap py-4 pl-3 pr-4 text-sm font-medium sm:pr-6'>
                  <Toggle
                    enabled={userBrand.isMenuVisible}
                    onChange={() =>
                      handleToggleVisibility(
                        userBrand,
                        !userBrand.isMenuVisible,
                      )
                    }
                    disabled={updatingBrandId === userBrand.id}
                    className={
                      updatingBrandId === userBrand.id ? 'opacity-50' : ''
                    }
                  />
                </td>
                <td className='whitespace-nowrap py-4 pl-3 pr-4 text-sm font-medium sm:pr-6'>
                  <Button
                    title='Go to Brand'
                    size={ButtonSize.medium}
                    colour={ButtonColor.primary}
                    styleType={ButtonStyle.fill}
                    onClick={() => handleLoginToBrand(userBrand.brand.id)}
                    type='button'
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {pagination && <Pagination pagination={pagination} setPage={setPage} />}
    </div>
  )
}

export { List }
